import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { getAllIdeaUsers } from "../../Utils/apiServices";
import {
  extractIdeasData,
  mapIdeaRecordToCardData,
  sortIdeasByStatus,
  formatIdeaStatusLabel,
  getIdeaStatusBadgeClasses,
  getIdeaFlowStageTheme,
  getActiveStageIndexFromCurrentStep,
} from "../../Container/IdeasReview/utils";
import Table from "../Table";
import Avatar from "../Avatar";
import { Typography } from "../Typography";

// True for the idea's owner row (the primary student), across API casings.
const isPrimaryStudentRow = (row) =>
  ["PRIMARY_STUDENT", "primary_student", "student"].includes(
    String(row?.role || "")
  );

// Reduce many idea_user rows to one per idea, preferring the primary student's
// row (so the admin list shows the owner + primary mentor, never a duplicate
// created by a secondary mentor or co-founder accepting the idea).
const dedupeByIdea = (rows) => {
  const byIdea = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const ideaId = row?.idea?.id ?? row?.id;
    if (ideaId == null) return;
    const existing = byIdea.get(ideaId);
    if (
      !existing ||
      (isPrimaryStudentRow(row) && !isPrimaryStudentRow(existing))
    ) {
      byIdea.set(ideaId, row);
    }
  });
  return Array.from(byIdea.values());
};

const getStudentDisplayName = (idea) => {
  const student = idea.primaryStudent;
  if (student) {
    const name = `${student.first_name || ""} ${student.last_name || ""}`.trim();
    if (name) return name;
    if (student.email) return student.email;
  }
  const tag = (idea.tags || []).find((t) => t.role === "student");
  return tag?.label || "—";
};

const IdeaList = ({ roleLabel = "Ideas" }) => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchIdeas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllIdeaUsers();
      const ideasData = extractIdeasData(response);
      // A single idea has several idea_user rows (primary student, secondary
      // students, secondary mentors). Collapse them to one row per idea — the
      // primary student's, which carries the primary mentor — so a secondary
      // mentor accepting doesn't surface the idea as a duplicate record.
      const dedupedData = dedupeByIdea(ideasData);
      const mapped = sortIdeasByStatus(
        dedupedData.map(mapIdeaRecordToCardData).filter(Boolean)
      );
      setIdeas(mapped);
    } catch (error) {
      console.error("Error fetching ideas:", error);
      setIdeas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  const statusOptions = useMemo(() => {
    const statuses = new Set(ideas.map((i) => i.status).filter(Boolean));
    return Array.from(statuses).sort();
  }, [ideas]);

  const filteredIdeas = useMemo(() => {
    let filtered = ideas;

    if (statusFilter) {
      filtered = filtered.filter(
        (idea) => (idea.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((idea) => {
        const title = (idea.title || "").toLowerCase();
        const category = (idea.category || "").toLowerCase();
        const status = (idea.status || "").toLowerCase();
        const student = getStudentDisplayName(idea).toLowerCase();
        const impact = (idea.primaryImpact || "").toLowerCase();
        const step = (idea.stepName || "").toLowerCase();
        const mentor = (idea.mentor?.name || "").toLowerCase();
        return (
          title.includes(searchLower) ||
          category.includes(searchLower) ||
          status.includes(searchLower) ||
          student.includes(searchLower) ||
          mentor.includes(searchLower) ||
          impact.includes(searchLower) ||
          step.includes(searchLower) ||
          String(idea.id).includes(searchLower)
        );
      });
    }

    return filtered;
  }, [ideas, searchTerm, statusFilter]);

  const tableData = useMemo(
    () =>
      filteredIdeas.map((idea) => ({
        key: String(idea.id),
        id: idea.id,
        title: idea.title || "—",
        image: idea.image,
        studentName: getStudentDisplayName(idea),
        studentId: idea.studentId,
        studentAvatar:
          idea.primaryStudent?.profile_picture_url ||
          (idea.tags || []).find((t) => t.role === "student")?.avatar ||
          null,
        studentEmail: idea.primaryStudent?.email || "",
        mentorName: idea.mentor?.name || "—",
        mentorAvatar: idea.mentor?.avatar || null,
        mentorEmail: idea.mentor?.email || "",
        stepName: idea.stepName || "—",
        status: idea.status || "",
        category: idea.category || "—",
        primaryImpact: idea.primaryImpact || "—",
        submissionDate: idea.submissionDate || "—",
        _idea: idea,
      })),
    [filteredIdeas]
  );

  const handleViewIdea = useCallback(
    (idea) => {
      navigate(`/ideas/${idea.id}`, {
        state: {
          primaryStudent: idea.primaryStudent,
          mentor: idea.mentor,
          initialStage: getActiveStageIndexFromCurrentStep(idea.currentStep ?? 1),
        },
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        header: "Idea",
        key: "title",
        accessor: "title",
        render: (title, record) => (
          <div className="flex items-center gap-3 min-w-[200px] max-w-[280px]">
            {record.image && (
              <img
                src={record.image}
                alt=""
                className="h-10 w-14 shrink-0 rounded-lg object-cover border border-slate-200"
              />
            )}
            <div className="min-w-0">
              <Typography as="p" className="text-sm font-semibold text-slate-900 truncate">
                {title}
              </Typography>
              <Typography as="p" className="text-xs text-slate-500">
                ID {record.id}
              </Typography>
            </div>
          </div>
        ),
      },
      {
        header: "Student",
        key: "studentName",
        accessor: "studentName",
        render: (name, record) =>
          record.studentId && name !== "—" ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/users/${record.studentId}`);
              }}
              className="flex items-center gap-2 text-left hover:opacity-80"
            >
              <Avatar
                src={record.studentAvatar}
                firstName={name?.split(" ")[0]}
                lastName={name?.split(" ").slice(1).join(" ")}
                email={record.studentEmail}
                alt={name}
                size="w-8 h-8"
              />
              <span className="text-sm font-medium text-violet-700 hover:underline truncate max-w-[140px]">
                {name}
              </span>
            </button>
          ) : (
            <Typography as="span" className="text-sm text-slate-400">
              {name}
            </Typography>
          ),
      },
      {
        header: "Mentor",
        key: "mentorName",
        accessor: "mentorName",
        render: (name, record) =>
          name !== "—" ? (
            <div className="flex items-center gap-2 min-w-[120px]">
              <Avatar
                src={record.mentorAvatar}
                firstName={name?.split(" ")[0]}
                lastName={name?.split(" ").slice(1).join(" ")}
                email={record.mentorEmail}
                alt={name}
                size="w-8 h-8"
              />
              <span className="text-sm text-slate-800 truncate max-w-[140px]">{name}</span>
            </div>
          ) : (
            <Typography as="span" className="text-sm text-slate-400">
              —
            </Typography>
          ),
      },
      {
        header: "Stage",
        key: "stepName",
        accessor: "stepName",
        render: (stepName) => {
          const theme = getIdeaFlowStageTheme(stepName);
          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${theme.badge}`}
            >
              <Icon icon="lucide:layers" className="h-3 w-3" />
              {stepName}
            </span>
          );
        },
      },
      {
        header: "Status",
        key: "status",
        accessor: "status",
        render: (status) => {
          if (!status) return "—";
          const badge = getIdeaStatusBadgeClasses(status);
          return (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${badge.bg} ${badge.text} ${badge.border}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} />
              {formatIdeaStatusLabel(status)}
            </span>
          );
        },
      },
      {
        header: "Category",
        key: "category",
        accessor: "category",
        render: (v) => (
          <Typography
            as="span"
            className="text-sm text-slate-700 line-clamp-2 max-w-[200px]"
            title={v !== "—" ? v : undefined}
          >
            {v}
          </Typography>
        ),
      },
      {
        header: "Impact",
        key: "primaryImpact",
        accessor: "primaryImpact",
      },
      {
        header: "Submitted",
        key: "submissionDate",
        accessor: "submissionDate",
      },
      {
        header: "Actions",
        key: "actions",
        accessor: "id",
        sortable: false,
        render: (_id, record) => (
          <button
            type="button"
            onClick={() => handleViewIdea(record._idea)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700 hover:underline"
          >
            Review
            <Icon icon="lucide:arrow-right" className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [navigate, handleViewIdea]
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-8 w-48 bg-slate-200 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Typography as="h2" className="text-2xl font-semibold text-primary-900">
            {roleLabel}
          </Typography>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            {ideas.length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Total ideas
          </Typography>
          <Typography as="span" className="text-2xl font-bold text-primary-900">
            {ideas.length}
          </Typography>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Showing
          </Typography>
          <Typography as="span" className="text-2xl font-bold text-slate-700">
            {filteredIdeas.length}
          </Typography>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Active
          </Typography>
          <Typography as="span" className="text-2xl font-bold text-green-600">
            {ideas.filter((i) => (i.status || "").toUpperCase() === "ACTIVE").length}
          </Typography>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <Typography as="span" className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            With student
          </Typography>
          <Typography as="span" className="text-2xl font-bold text-purple-600">
            {ideas.filter((i) => i.studentId).length}
          </Typography>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by title, student, mentor, stage, category, status..."
            className="w-full px-3 py-2.5 pl-9 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <Icon
            icon="lucide:search"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <Icon icon="lucide:x" className="h-4 w-4" />
            </button>
          )}
        </div>
        {statusOptions.length > 0 && (
          <div className="w-full sm:w-52">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full h-[42px] px-3 text-sm border border-slate-200 rounded-lg bg-slate-50 text-primary-900 focus:border-brand-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue"
            >
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatIdeaStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <Table
          columns={columns}
          data={tableData}
          rowKey="key"
          emptyMessage={
            searchTerm || statusFilter
              ? "No ideas match your filters"
              : "No ideas found"
          }
          scroll={{ x: 1100 }}
        />
        {(searchTerm || statusFilter) && tableData.length === 0 && (
          <div className="flex justify-center pb-6">
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
              }}
              className="text-sm font-medium text-brand-700 hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeaList;
